/**
 * The ONE structured send-failure record (#333 §1.4, folds in #370 / #371):
 * produced the same way by every engine and written to `messagelogs.metadata`
 * as the `sendFail*` keys.
 *
 * `sanitizeFailure` is the only way to build a record; `failureToMetadata` and
 * `failureToReason` read only what it kept. #333 slice 2: the Baileys pod's
 * common text path writes it (`sendFail*`); `runSendProtocol` as the single
 * writer for both engines is a later slice.
 *
 * Pure and dependency-free (Node + browser).
 */

import { ENGINE_ERROR_CODES, type EngineErrorCode } from '../interfaces/whatsapp-tools-commands.interface';
import { SEND_FAIL_REASONS } from './send-fail-reason';

/** Where the send failed, the same meaning in every engine:
 *  `prep` build/resolve before anything left, `upload` media upload, `send` the
 *  relay threw with no ack, `ack` the server answered with an error, `cancelled`
 *  send window / lock / deleted message. */
export type SendFailStage = 'prep' | 'upload' | 'send' | 'ack' | 'cancelled';

/** Which adapter produced the record. `wa-web-native` = today's D0 path;
 *  `unknown` = an engine value the sanitizer did not recognise (never written by an adapter). */
export type SendFailEngine = 'baileys-pod' | 'wa-web' | 'wa-web-native' | 'unknown';

export interface SendFailure {
    stage: SendFailStage;
    engine: SendFailEngine;
    /** The normalised, engine-agnostic code (existing ENGINE_ERROR_CODES). */
    code: EngineErrorCode;
    /** Engine-native token only ('ERROR_UNKNOWN', server error attr '463', nack
     *  code, Baileys DisconnectReason, WA upload kind). Never content. */
    nativeCode?: string;
    /** Last known ack (-1 / 0 / 1..3) at the failure point. */
    ack?: number;
    /** Sorted KEY NAMES of the engine's result/ack object, never values. */
    resultKeys?: string[];
    /** `Error.name` / constructor name only ('TypeError', 'Boom'). */
    errorClass?: string;
    /** Did bytes reach WhatsApp's server? `prep`/`upload`/`cancelled` → false,
     *  `ack` → true, `send` → 'unknown' unless proven. 'unknown' is never resent blind. */
    onWire: boolean | 'unknown';
    retryAfterSec?: number;
}

/** `SendFailure` field → its `messagelogs.metadata` key (one set for all engines). */
export const SEND_FAIL_METADATA_KEYS = {
    stage: 'sendFailStage',
    engine: 'sendFailEngine',
    code: 'sendFailCode',
    nativeCode: 'sendFailNativeCode',
    ack: 'sendFailAck',
    resultKeys: 'sendFailResultKeys',
    errorClass: 'sendFailErrorClass',
    onWire: 'sendFailOnWire',
} as const satisfies Partial<Record<keyof SendFailure, string>>;

/** The `messagelogs.metadata` shape of a `SendFailure`. */
export interface SendFailMetadata {
    sendFailStage: SendFailStage;
    sendFailEngine: SendFailEngine;
    sendFailCode: EngineErrorCode;
    sendFailNativeCode?: string;
    sendFailAck?: number;
    sendFailResultKeys?: string[];
    sendFailErrorClass?: string;
    sendFailOnWire: boolean | 'unknown';
}

/** What a throw site hands `sanitizeFailure`. The loose fields are read, never
 *  copied: `resultKeys` takes an object's KEY NAMES (or a list of names) and
 *  `errorClass` an error's class name, so a caller can pass the raw ack attrs or
 *  the caught error without a value reaching the record. */
export interface SendFailureInput {
    stage: SendFailStage;
    engine: SendFailEngine;
    code: EngineErrorCode;
    /** A token (string or number), e.g. the ack `error` attr. Dropped unless it passes the token rule. */
    nativeCode?: unknown;
    ack?: unknown;
    resultKeys?: unknown;
    errorClass?: unknown;
    /** Overrides the stage default only when the adapter proves it. */
    onWire?: boolean | 'unknown';
    retryAfterSec?: unknown;
}

const STAGES: ReadonlySet<string> = new Set<SendFailStage>(['prep', 'upload', 'send', 'ack', 'cancelled']);
const ENGINES: ReadonlySet<string> = new Set<SendFailEngine>(['baileys-pod', 'wa-web', 'wa-web-native']);
const ENGINE_CODES: ReadonlySet<string> = new Set(ENGINE_ERROR_CODES);
const MAX_RESULT_KEYS = 20;
const MAX_RETRY_AFTER_SEC = 86400;
const SEND_FAIL_REASON_MAX = 300;

/** Native tokens that may be kept besides a 1-4 digit code: WhatsApp server error
 *  texts and Baileys/wa-js status names. An ALLOWLIST: a charset rule cannot tell
 *  a name, a host or a hex id from a token, so anything not listed is dropped. */
const NATIVE_CODE_TOKENS: ReadonlySet<string> = new Set([
    'bad-request', 'conflict', 'forbidden', 'gone', 'internal-server-error', 'item-not-found',
    'not-acceptable', 'not-allowed', 'not-authorized', 'rate-overlimit', 'recipient-not-found',
    'resource-limit', 'service-unavailable', 'unavailable', 'timeout', 'ack-timeout',
    'ERROR_UNKNOWN', 'ERROR_NO_MESSAGE', 'ERROR_UPLOAD', 'ERROR_NOT_ON_WHATSAPP',
    // #371: the closed send-fail reasons WA Web's own send job / upload reported.
    ...SEND_FAIL_REASONS,
]);

/** Ack attribute names (never values) the engines read. */
const RESULT_KEY_NAMES: ReadonlySet<string> = new Set([
    'addressing_mode', 'backoff', 'category', 'class', 'count', 'error', 'from', 'id', 'offline',
    'participant', 'peer_participant_pn', 'phash', 'recipient', 'refusal', 'retry', 'sender_lid',
    'sender_pn', 't', 'to', 'type',
]);

/** Error class names that may be kept (standard JS, the libraries a send touches, ours). */
const ERROR_CLASS_NAMES: ReadonlySet<string> = new Set([
    'Error', 'TypeError', 'RangeError', 'SyntaxError', 'ReferenceError', 'EvalError', 'URIError',
    'AggregateError', 'DOMException', 'AbortError', 'TimeoutError', 'Boom', 'FetchError',
    'AxiosError', 'SqliteError', 'EngineToolError', 'MongoError', 'MongoServerError',
]);

export function isSendFailStage(v: unknown): v is SendFailStage {
    return typeof v === 'string' && STAGES.has(v);
}

export function isSendFailEngine(v: unknown): v is Exclude<SendFailEngine, 'unknown'> {
    return typeof v === 'string' && ENGINES.has(v);
}

export function isEngineErrorCode(v: unknown): v is EngineErrorCode {
    return typeof v === 'string' && ENGINE_CODES.has(v);
}

function nativeCodeOf(value: unknown): string | undefined {
    const s = typeof value === 'number' && Number.isInteger(value) ? String(value) : value;
    if (typeof s !== 'string') return undefined;
    return /^\d{1,4}$/.test(s) || NATIVE_CODE_TOKENS.has(s) ? s : undefined;
}

function errorClassOf(value: unknown): string | undefined {
    const name = typeof value === 'string' ? value
        : value instanceof Error ? (value.name !== 'Error' ? value.name : value.constructor?.name ?? value.name)
        : undefined;
    return name !== undefined && ERROR_CLASS_NAMES.has(name) ? name : undefined;
}

/** One hostile field (a throwing getter / Proxy) loses only itself. */
function safely<T>(read: () => T | undefined): T | undefined {
    try {
        return read();
    } catch {
        return undefined;
    }
}

function resultKeysOf(value: unknown): string[] | undefined {
    const names: unknown[] | undefined = Array.isArray(value) ? value : value && typeof value === 'object' ? Object.keys(value) : undefined;
    if (!names) return undefined;
    const kept = [...new Set(names.filter((k): k is string => typeof k === 'string' && RESULT_KEY_NAMES.has(k)))].sort();
    return kept.length ? kept.slice(0, MAX_RESULT_KEYS) : undefined;
}

function intIn(value: unknown, min: number, max: number): number | undefined {
    return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : undefined;
}

/** Did bytes reach WhatsApp, by stage: nothing left before the relay, the server answered an ack, a relay that threw is unknown. */
function onWireForStage(stage: SendFailStage): boolean | 'unknown' {
    if (stage === 'ack') return true;
    if (stage === 'send') return 'unknown';
    return false;
}

/** What a hostile or malformed input collapses to: no detail, on-wire unknown (never resent blind). */
const FALLBACK_FAILURE: SendFailure = { stage: 'send', engine: 'unknown', code: 'engine_error', onWire: 'unknown' };

/**
 * The only way to build a `SendFailure`. Keeps allowlisted tokens and key names,
 * never values: no JIDs, phone numbers, message/stanza ids, URLs, names or
 * content survive (§1.4). An unknown stage is treated as `send` (on-wire unknown,
 * never resent blind), an unknown code as `engine_error`, an unknown engine as
 * `unknown`. Never throws (it runs inside error paths) and is idempotent.
 */
export function sanitizeFailure(raw: SendFailureInput): SendFailure {
    try {
        const stage: SendFailStage = isSendFailStage(raw.stage) ? raw.stage : 'send';
        const code: EngineErrorCode = isEngineErrorCode(raw.code) ? raw.code : 'engine_error';
        const engine: SendFailEngine = isSendFailEngine(raw.engine) ? raw.engine : 'unknown';
        const onWire = raw.onWire === true || raw.onWire === false || raw.onWire === 'unknown' ? raw.onWire : onWireForStage(stage);
        const out: SendFailure = { stage, engine, code, onWire };
        const nativeCode = safely(() => nativeCodeOf(raw.nativeCode));
        if (nativeCode !== undefined) out.nativeCode = nativeCode;
        const ack = intIn(raw.ack, -1, 5);
        if (ack !== undefined) out.ack = ack;
        const resultKeys = safely(() => resultKeysOf(raw.resultKeys));
        if (resultKeys) out.resultKeys = resultKeys;
        const errorClass = safely(() => errorClassOf(raw.errorClass));
        if (errorClass !== undefined) out.errorClass = errorClass;
        const retryAfterSec = intIn(raw.retryAfterSec, 0, MAX_RETRY_AFTER_SEC);
        if (retryAfterSec !== undefined) out.retryAfterSec = retryAfterSec;
        return out;
    } catch {
        return { ...FALLBACK_FAILURE };
    }
}

/** The `messagelogs.metadata` keys of a record (absent fields stay absent). Re-sanitizes, so a hand-built record cannot leak. */
export function failureToMetadata(f: SendFailure): SendFailMetadata {
    const s = sanitizeFailure(f);
    const out: SendFailMetadata = { sendFailStage: s.stage, sendFailEngine: s.engine, sendFailCode: s.code, sendFailOnWire: s.onWire };
    if (s.nativeCode !== undefined) out.sendFailNativeCode = s.nativeCode;
    if (s.ack !== undefined) out.sendFailAck = s.ack;
    if (s.resultKeys) out.sendFailResultKeys = s.resultKeys;
    if (s.errorClass !== undefined) out.sendFailErrorClass = s.errorClass;
    return out;
}

/** `<stage>:<code>[:<nativeCode>]`, from sanitized fields only (≤ 300 chars). */
export function failureToReason(f: SendFailure): string {
    const s = sanitizeFailure(f);
    return [s.stage, s.code, s.nativeCode].filter(Boolean).join(':').slice(0, SEND_FAIL_REASON_MAX);
}

/**
 * The ONE validated boundary for an untyped `sendFail*` record (a log body from
 * an engine, a stored doc): reads each key with a guard, re-sanitizes, and
 * returns the metadata, or `undefined` when there is no record or it names an
 * unknown engine / lacks a stage or code (a record we cannot trust is dropped,
 * not repaired). No cast anywhere: callers pass `unknown`.
 */
export function sendFailureMetadataFrom(raw: unknown): SendFailMetadata | undefined {
    try {
        if (!raw || typeof raw !== 'object') return undefined;
        const field = (key: string): unknown => Object.entries(raw).find(([k]) => k === key)?.[1];
        const stage = field(SEND_FAIL_METADATA_KEYS.stage);
        const engine = field(SEND_FAIL_METADATA_KEYS.engine);
        const code = field(SEND_FAIL_METADATA_KEYS.code);
        if (!isSendFailStage(stage) || !isSendFailEngine(engine) || !isEngineErrorCode(code)) return undefined;
        const onWire = field(SEND_FAIL_METADATA_KEYS.onWire);
        return failureToMetadata(sanitizeFailure({
            stage,
            engine,
            code,
            nativeCode: field(SEND_FAIL_METADATA_KEYS.nativeCode),
            ack: field(SEND_FAIL_METADATA_KEYS.ack),
            resultKeys: field(SEND_FAIL_METADATA_KEYS.resultKeys),
            errorClass: field(SEND_FAIL_METADATA_KEYS.errorClass),
            onWire: onWire === true || onWire === false || onWire === 'unknown' ? onWire : undefined,
        }));
    } catch {
        return undefined;
    }
}
